import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useRef } from 'react';
import { forEach as _forEach, forIn as _forIn, includes as _includes, isEmpty as _isEmpty, uniq as _uniq } from 'lodash';
import PropTypes from 'prop-types';
import memoize from 'moize';
import Tile from './Tile';

/**
 * @param uniqueLayerKey {string}
 * @param tile {Object}
 * @return {string}
 */
import { jsx as _jsx } from "react/jsx-runtime";
function getTileKey(uniqueLayerKey, tile) {
  return "".concat(uniqueLayerKey, "_").concat(tile.level, "_").concat(typeof tile.tile === 'string' ? tile.tile : JSON.stringify(tile.tile));
}

/**
 * @param uniqueLayerKey {string}
 * @param tiles {Array}
 * @param fidColumnName {string}
 * @param selections {Object}
 * @return {Object} groupedFeatures - a collection of features grouped by tile key, groupedFeaturesKeys - a collection of feature keys grouped by tile key
 */
export function getFeaturesGroupedByTileKey(uniqueLayerKey, tiles, fidColumnName, selections) {
  var groupedFeatures = [];
  var groupedFeatureKeys = [];
  var selected = {};
  _forEach(tiles, function (tile) {
    // TODO pass featureKeys or filters
    var key = getTileKey(uniqueLayerKey, tile);
    var tileFeatureKeys = [];
    if (tile.features) {
      _forEach(tile.features, function (feature) {
        var fid = feature.id || feature.properties[fidColumnName];
        if (selections) {
          _forIn(selections, function (selection, selectionKey) {
            if (selection.keys && _includes(selection.keys, fid)) {
              var _selected$selectionKe;
              if ((_selected$selectionKe = selected[selectionKey]) !== null && _selected$selectionKe !== void 0 && _selected$selectionKe.features) {
                selected[selectionKey].features[fid] = feature;
                selected[selectionKey].featureKeys.push(fid);
              } else {
                selected[selectionKey] = {
                  features: _defineProperty({}, fid, feature),
                  featureKeys: [fid],
                  level: tile.level
                };
              }
            }
          });
        }
        tileFeatureKeys.push(fid);
      });
    }
    groupedFeatures.push(_objectSpread(_objectSpread({}, tile), {}, {
      key: key
    }));
    groupedFeatureKeys.push({
      key: key,
      featureKeys: tileFeatureKeys
    });
  });
  if (!_isEmpty(selected)) {
    _forIn(selected, function (selection, selectionKey) {
      var key = "".concat(uniqueLayerKey, "_").concat(selectionKey, "_").concat(selection.level);
      groupedFeatures.unshift({
        key: key,
        features: Object.values(selection.features),
        level: selection.level,
        withSelectedFeaturesOnly: true
      });
      groupedFeatureKeys.unshift({
        key: key,
        featureKeys: _uniq(selection.featureKeys)
      });
    });
  }
  return {
    groupedFeatures: groupedFeatures,
    groupedFeatureKeys: groupedFeatureKeys
  };
}
var TiledVectorLayer = function TiledVectorLayer(_ref) {
  var _data$groupedFeatures;
  var fidColumnName = _ref.fidColumnName,
    tiles = _ref.tiles,
    layerKey = _ref.layerKey,
    selected = _ref.selected,
    uniqueLayerKey = _ref.uniqueLayerKey,
    renderingTechnique = _ref.renderingTechnique,
    view = _ref.view,
    zoom = _ref.zoom,
    width = _ref.width,
    height = _ref.height,
    crs = _ref.crs,
    boxRangeRange = _ref.boxRangeRange,
    style = _ref.style,
    mapKey = _ref.mapKey,
    onClick = _ref.onClick,
    opacity = _ref.opacity,
    resources = _ref.resources,
    type = _ref.type,
    zIndex = _ref.zIndex,
    hovered = _ref.hovered,
    hoverable = _ref.hoverable,
    hoveredStyleDefinition = _ref.hoveredStyleDefinition,
    pointAsMarker = _ref.pointAsMarker,
    selectable = _ref.selectable;
  var getFeaturesGroupedByTileKeyMemoized = useRef(memoize(getFeaturesGroupedByTileKey));
  var data = getFeaturesGroupedByTileKeyMemoized.current(uniqueLayerKey, tiles, fidColumnName, selected);
  if ((_data$groupedFeatures = data.groupedFeatures) !== null && _data$groupedFeatures !== void 0 && _data$groupedFeatures.length) {
    return data.groupedFeatures.map(function (tile) {
      return /*#__PURE__*/_jsx(Tile, {
        tileKey: tile.key,
        featureKeysGroupedByTileKey: data.groupedFeatureKeys,
        renderingTechnique: renderingTechnique,
        level: tile.level,
        tile: tile.tile,
        features: tile.features,
        withSelectedFeaturesOnly: tile.withSelectedFeaturesOnly,
        view: view,
        zoom: zoom,
        width: width,
        height: height,
        crs: crs,
        boxRangeRange: boxRangeRange,
        style: style,
        mapKey: mapKey,
        layerKey: layerKey,
        uniqueLayerKey: uniqueLayerKey,
        onClick: onClick,
        opacity: opacity,
        resources: resources,
        type: type,
        zIndex: zIndex,
        fidColumnName: fidColumnName,
        hovered: hovered,
        hoverable: hoverable,
        hoveredStyleDefinition: hoveredStyleDefinition,
        pointAsMarker: pointAsMarker,
        selectable: selectable,
        selected: selected
      }, tile.key);
    });
  } else {
    return null;
  }
};
TiledVectorLayer.propTypes = {
  fidColumnName: PropTypes.string,
  tiles: PropTypes.array,
  layerKey: PropTypes.string,
  uniqueLayerKey: PropTypes.string,
  selected: PropTypes.object,
  view: PropTypes.object,
  zoom: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  crs: PropTypes.string,
  boxRangeRange: PropTypes.array,
  style: PropTypes.object,
  mapKey: PropTypes.string,
  onClick: PropTypes.func,
  opacity: PropTypes.number,
  resources: PropTypes.object,
  type: PropTypes.string,
  zIndex: PropTypes.number,
  hovered: PropTypes.object,
  hoverable: PropTypes.bool,
  hoveredStyleDefinition: PropTypes.object,
  pointAsMarker: PropTypes.bool,
  selectable: PropTypes.bool,
  renderingTechnique: PropTypes.string
};
export default TiledVectorLayer;